'use client';

import React, { useState, useEffect } from 'react';
import { EyeOff, Copy, ArrowUp, ArrowDown, Trash2, Lock, Plus } from 'lucide-react';
import Link from '@/core/i18n/navigation';
import { Button } from '@/core/components/ui/button';
import { cn } from '@/core/utils/cn';
import { useSliderState } from './hooks/useSliderState';
import {
  computeBaseStyles,
  computeWrapperStyles,
  computeBlockStyles,
  computeInnerElementStyles,
  computeResponsiveTypography,
  getShadowClass,
  getAnimationClass,
  CARD_ANIMATIONS,
  getHoverClass,
  HOVER_SCOPED_TO_INNER_ELEMENT,
  styleObjectToCssText,
  MAX_WIDTH_PX,
  isBlockEmptyForProduct,
} from './blockHelpers';
import { DEMO_PRODUCT } from './demoProduct';
import { ProductProvider } from './ProductContext';
import { CatalogProvider } from './CatalogContext';
import { blockDisplayName, DEPTH_INDICATOR_COLORS } from './blockMeta';
import { BLOCK_COMPONENTS } from './blockRegistry';
import { useSiteConfig } from '@/core/components/providers/SiteConfigProvider';
import { isShopBlock, isSiteMode } from '@/core/entitlements';
import { AnimatedBlockDiv, getScrollFx } from './AnimatedBlockDiv';
import { ALL_BREAKPOINTS, isHiddenOn, type Breakpoint } from './breakpoints';
import { VisibleBreakpointsProvider } from './VisibleBreakpoints';
import { useInitialViewport } from './InitialViewportContext';
import { FirstProductGridContext, findFirstProductGridId } from './FirstProductGridContext';

interface BlockRendererProps {
  blocks: any[];
  // Reading-width constraint for non-full-width blocks (e.g. article pages
  // pass a narrower 'max-w-4xl' for a comfortable reading column) — a block
  // with its own "Pełna szerokość" toggle still ignores this and goes
  // `w-full`, same as on a regular page.
  maxWidthClass?: string;
  viewport?: 'desktop' | 'tablet' | 'mobile';
  product?: any;
  category?: any;
  selectedBlockId?: string | null;
  onSelectBlock?: (id: string | null) => void;
  onUpdateBlockStyle?: (id: string, key: string, value: any, isResponsive?: boolean) => void;
  onUpdateBlockContent?: (id: string, content: string) => void;
  isAdmin?: boolean;
  isBuilderCanvas?: boolean;
  isNested?: boolean;
  orderOverrides?: Record<string, number>;
  initialQuery?: string;
  initialFilters?: any;
  initialPage?: number;
  initialTotal?: number;
  initialSort?: string;
  initialBlockData?: Record<string, any>;
  depth?: number;
  onDuplicateBlock?: (id: string) => void;
  onMoveBlockUp?: (id: string) => void;
  onMoveBlockDown?: (id: string) => void;
  onRemoveBlock?: (id: string) => void;
  /** See BlockProps.authMode — forwarded to every block, only 'auth-box' reads it. */
  authMode?: 'login' | 'register' | 'reset';
  /** Lets a first-visible container/row pass LCP priority into its first visible descendant —
   *  `true` for every breakpoint, or only the breakpoints on which the container itself is first. */
  prioritizeFirstVisible?: boolean | readonly Breakpoint[];
  /** With prioritizeFirstVisible: every visible child is a candidate, not only the first — a row's
   *  columns sit side by side (or stack directly under each other on phones), so in a hero row the
   *  image in the 2nd column is just as much "first screen" as the text in the 1st. */
  prioritizeAllVisible?: boolean;
}

export const BlockRenderer: React.FC<BlockRendererProps> = ({
  blocks,
  maxWidthClass = 'max-w-7xl',
  viewport: viewportProp,
  product,
  category,
  selectedBlockId,
  onSelectBlock,
  isAdmin = false,
  isBuilderCanvas = false,
  isNested = false,
  onUpdateBlockStyle,
  onUpdateBlockContent,
  orderOverrides,
  initialQuery = '',
  initialFilters,
  initialPage,
  initialTotal,
  initialSort,
  initialBlockData,
  depth = 0,
  onDuplicateBlock,
  onMoveBlockUp,
  onMoveBlockDown,
  onRemoveBlock,
  authMode,
  prioritizeFirstVisible = false,
  prioritizeAllVisible = false,
}) => {
  // `viewport` is also the server-provided initial storefront viewport. Starting
  // from the same value on the server and in the browser keeps hydration stable;
  // media queries below correct unusual window sizes without reading layout
  // geometry (`innerWidth` was causing a forced reflow during first paint).
  // Top-level storefront renderers get no prop — they start from the storefront layout's
  // request-header guess (InitialViewportProvider), so a phone is server-rendered with its
  // mobile styles instead of desktop ones that then jump on hydration (CLS).
  const initialViewport = useInitialViewport();
  const viewport = viewportProp ?? (isAdmin ? undefined : initialViewport) ?? 'desktop';
  const [currentViewport, setCurrentViewport] = useState<'desktop' | 'tablet' | 'mobile'>(viewport);
  // A website without the shop module: shop blocks (products, cart, product template parts...)
  // render nothing live and a notice in the editor, so a page keeps working after shop → site.
  const siteMode = isSiteMode(useSiteConfig());

  useEffect(() => {
    if (isAdmin) {
      setCurrentViewport(viewport);
      return;
    }

    const mobileQuery = window.matchMedia('(max-width: 767px)');
    const tabletQuery = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');
    const syncViewport = () => {
      const nextViewport = mobileQuery.matches ? 'mobile' : tabletQuery.matches ? 'tablet' : 'desktop';
      setCurrentViewport((previous) => previous !== nextViewport ? nextViewport : previous);
    };

    syncViewport();
    mobileQuery.addEventListener('change', syncViewport);
    tabletQuery.addEventListener('change', syncViewport);
    return () => {
      mobileQuery.removeEventListener('change', syncViewport);
      tabletQuery.removeEventListener('change', syncViewport);
    };
  }, [isAdmin, viewport]);

  const { activeSlides, setActiveSlides, pausedSliders, toggleSliderPause } = useSliderState(blocks, currentViewport);
  const [isResizing, setIsResizing] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  useEffect(() => {
    if (!isResizing || !onUpdateBlockStyle) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStartX;
      const containerWidth = document.querySelector('.is-admin-renderer')?.clientWidth || 1200;
      const targetBlock = blocks.find((b) => b.id === isResizing);
      const textAlign =
        targetBlock?.styles?.[currentViewport]?.textAlign || targetBlock?.styles?.textAlign || 'center';
      const deltaPercent = (deltaX / containerWidth) * 100 * (textAlign === 'center' ? 2 : 1);
      const newWidth = Math.min(Math.max(resizeStartWidth + deltaPercent, 10), 100);
      onUpdateBlockStyle(isResizing, 'width', Math.round(newWidth), true);
    };

    const handleMouseUp = () => setIsResizing(null);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStartX, resizeStartWidth, onUpdateBlockStyle, blocks, currentViewport]);

  const resolveTags = (content: any): string => {
    if (!content) return '';
    let str = typeof content === 'string'
      ? content
      : (typeof content === 'object' && content !== null
          ? (content.text || content.html || content.content || (typeof content.url === 'string' ? content.url : ''))
          : String(content));
    if (!str || typeof str !== 'string') return '';
    let res = str;
    if (product) {
      res = res.replace(/\{\{product\.(.*?)\}\}/g, (match, key) => {
        if (key === 'price') return `${product.price?.toFixed(2)} PLN`;
        if (key === 'lowest_price_30_days' || key === 'omnibus_price') return product.sales_price ? `${product.sales_price?.toFixed(2)} PLN` : `${product.price?.toFixed(2)} PLN`;
        return product[key] !== undefined ? String(product[key]) : match;
      });
    }
    if (category) {
      res = res.replace(/\{\{category\.(.*?)\}\}/g, (match, key) => category[key] !== undefined ? String(category[key]) : match);
    }
    res = res.replace(/\{\{site\.name\}\}/g, 'ShopStag');
    return res;
  };

  if (!blocks || blocks.length === 0) return null;

  const activeProduct = product || (isAdmin ? DEMO_PRODUCT : null);
  const renderableBlocks = blocks.filter((b: any) => {
    if (!b || b.type === 'globals') return false;
    if (!isAdmin && siteMode && isShopBlock(b.type)) return false;
    if (!isAdmin && product && isBlockEmptyForProduct(b, product)) return false;
    return true;
  });

  // A page may deliberately have separate desktop/tablet/mobile hero blocks. The first array
  // item is then not necessarily the first *visible* item (Paputy Pupila uses a desktop slider
  // followed by a mobile slider). Record, per block, the breakpoints on which it is the first
  // visible one — its image is the LCP candidate only there. One that's first everywhere loads
  // eagerly; one that's first only on some loads 'deferred' (see ImageLoadPriority) — eager images
  // inside a display:none block still download, so both heroes used to download on every device.
  const candidateBreakpoints: readonly Breakpoint[] = !isNested || prioritizeFirstVisible === true
    ? ALL_BREAKPOINTS
    : Array.isArray(prioritizeFirstVisible) ? prioritizeFirstVisible : [];
  const lcpBreakpoints = new Map<string, Breakpoint[]>();
  if (!isAdmin) {
    candidateBreakpoints.forEach((breakpoint) => {
      const visible = renderableBlocks.filter((candidate: any) => !isHiddenOn(candidate.styles, breakpoint));
      const candidates = isNested && prioritizeAllVisible ? visible : visible.slice(0, 1);
      for (const candidate of candidates) {
        if (candidate?.id) lcpBreakpoints.set(candidate.id, [...(lcpBreakpoints.get(candidate.id) || []), breakpoint]);
      }
    });
  }

  const renderedBlocks = (
    <div className={cn(isNested ? 'contents' : 'w-full space-y-0', isAdmin && !isNested && 'is-admin-renderer')}>
      {renderableBlocks.map((block: any) => {
        const styles = block.styles || {};
        const currentStyles = { ...styles, ...(styles[currentViewport] || {}) };

        const isHidden =
          (styles.hideDesktop && currentViewport === 'desktop') ||
          (styles.hideTablet && currentViewport === 'tablet') ||
          (styles.hideMobile && currentViewport === 'mobile');

        const baseStyles = computeBaseStyles(currentStyles, styles, undefined, block.type);
        const wrapperStyles = computeWrapperStyles(block, currentStyles, baseStyles);
        const blockStyles = computeBlockStyles(currentStyles, baseStyles);
        // Live: typography that differs between viewports (e.g. a 60px desktop / 36px mobile hero
        // heading) goes through CSS variables set per breakpoint in this block's <style> below, so
        // the first paint already has the right size and hydration never changes it. Inline values
        // computed from currentViewport made every such text block shrink after hydration on
        // phones — the biggest CLS source PageSpeed reported. Admin keeps literal values (its
        // viewport is the editor's switcher, not a media query).
        const responsiveTypography = !isAdmin ? computeResponsiveTypography(block, styles) : null;
        const innerElementStyles = {
          ...computeInnerElementStyles(blockStyles),
          ...(responsiveTypography?.inlineOverrides || {}),
        };
        const hoverStyles = currentStyles.hoverColor
          ? { '--hover-color': currentStyles.hoverColor }
          : {};
        // Per-viewport: a merchant may want a block constrained on desktop but
        // full-bleed on mobile (or vice-versa) — currentStyles already merges
        // the active viewport's override over the base/desktop value.
        const isFullWidth = currentStyles.fullWidth === true;

        // Live rendering can't use the currentViewport/isFullWidth values above for the
        // wrapper's own box model: currentViewport is JS-detected from window.innerWidth,
        // which doesn't exist during SSR (falls back to 'desktop') and only corrects itself
        // after client hydration runs — so the very first HTML (and anything painted before
        // JS finishes loading) always used the desktop-merged wrapper styles regardless of
        // the visiting device, e.g. a block set narrower on desktop than mobile rendered
        // narrow on real phones until hydration caught up. Admin's viewport instead comes
        // from an explicit prop (the switcher), so it's unaffected and keeps using
        // currentStyles/wrapperStyles/isFullWidth above unchanged. Live gets both the mobile-
        // and desktop-merged wrapper styles baked into real `@media`-gated CSS instead, same
        // pattern HeroPackshotBlock already uses for its own belka — the browser picks the
        // right one on first paint, no JS/hydration required. Tablet folds into the desktop
        // (768px+) bucket here, same as every other real CSS breakpoint in this codebase
        // (Tailwind's `md:`, HeroPackshotBlock's own media query) — there's no separate real
        // CSS tablet range anywhere else to match.
        let respBlockClass = '';
        let respInnerClass = '';
        let respCss = '';
        if (!isAdmin) {
          const mobileMerged = { ...styles, ...(styles.mobile || {}) };
          const desktopMerged = { ...styles, ...(styles.tablet || {}), ...(styles.desktop || {}) };
          // Background picture: the base (phone) rule gets an optimized copy 1440 px wide — at least
          // the device pixels of any phone (430 px × DPR 3); the 768px+ rule keeps the original upload,
          // since a large retina screen may show more pixels than any fixed width.
          const mobileWrapper = computeWrapperStyles(block, mobileMerged, computeBaseStyles(mobileMerged, styles, undefined, block.type), 1440);
          const desktopWrapper = computeWrapperStyles(block, desktopMerged, computeBaseStyles(desktopMerged, styles, undefined, block.type));
          const mobileFullWidth = mobileMerged.fullWidth === true;
          const desktopFullWidth = desktopMerged.fullWidth === true;
          const safeId = String(block.id).replace(/[^a-zA-Z0-9_-]/g, '');
          respBlockClass = `bw-${safeId}`;
          respInnerClass = `bwi-${safeId}`;
          const maxWidthPx = MAX_WIDTH_PX[maxWidthClass] || 1280;
          // The 768px+ rule only needs what differs from the base (mobile) rule — it only ever
          // overrides, and a property set on mobile but absent on desktop already carried over
          // before. It used to repeat every declaration, usually identically: with ~200 blocks a
          // page that was a large share of the ~160 KB of inline <style> phones had to parse.
          const desktopOverrides = Object.fromEntries(
            Object.entries(desktopWrapper).filter(([key, value]) => (mobileWrapper as Record<string, unknown>)[key] !== value),
          );
          const desktopOverridesCss = styleObjectToCssText(desktopOverrides);
          respCss = `.${respBlockClass} { ${styleObjectToCssText(mobileWrapper)} }
${desktopOverridesCss ? `@media (min-width: 768px) { .${respBlockClass} { ${desktopOverridesCss} } }\n` : ''}.${respInnerClass} { width: 100%; height: 100%; padding-left: 16px; padding-right: 16px; ${mobileFullWidth ? 'max-width: none; margin-left: 0; margin-right: 0; padding-left: 0; padding-right: 0;' : `max-width: ${maxWidthPx}px; margin-left: auto; margin-right: auto;`} }
@media (min-width: 768px) { .${respInnerClass} { padding-left: 32px; padding-right: 32px; ${desktopFullWidth ? 'max-width: none; margin-left: 0; margin-right: 0; padding-left: 0; padding-right: 0;' : `max-width: ${maxWidthPx}px; margin-left: auto; margin-right: auto;`} } }`
            + (responsiveTypography?.css(`.${respBlockClass}`) || '');
        }

        const sharedProps = {
          block,
          currentStyles,
          blockStyles,
          innerElementStyles,
          viewport: currentViewport,
          isAdmin,
          isBuilderCanvas,
          isFirstBlock: lcpBreakpoints.has(block.id),
          firstBlockBreakpoints: lcpBreakpoints.get(block.id),
          selectedBlockId,
          onSelectBlock,
          product,
          category,
          onUpdateBlockStyle,
          onUpdateBlockContent,
          resolveTags,
          isNested,
          depth,
          onDuplicateBlock,
          onMoveBlockUp,
          onMoveBlockDown,
          onRemoveBlock,
          setIsResizing,
          setResizeStartX,
          setResizeStartWidth,
          activeSlides,
          setActiveSlides,
          pausedSliders,
          toggleSliderPause,
          initialData: initialBlockData?.[block.id],
          initialBlockData,
          authMode,
        };

        const isSelected = isAdmin && selectedBlockId === block.id;
        const domId = (block.settings?.customId && String(block.settings.customId).trim()) || `b-${block.id}`;

        return (
          <AnimatedBlockDiv
            key={block.id}
            id={domId}
            data-block-id={block.id}
            animationClass={block.type !== 'container' || (isNested && CARD_ANIMATIONS.has(styles.animation)) ? getAnimationClass(styles.animation) : undefined}
            animateOnScroll={!isAdmin}
            scrollFx={getScrollFx(styles)}
            onClick={(e) => {
              if (isAdmin && onSelectBlock) {
                e.stopPropagation();
                onSelectBlock(block.id);
              }
            }}
            className={cn(
              'block-wrapper relative',
              // Layout-affecting responsive values can change when the admin switches its
              // simulated viewport. Keep that editor nicety there, but never animate height,
              // padding or margin during live hydration — each animation frame counts as CLS.
              isAdmin && 'transition-all duration-200',
              !isAdmin && respBlockClass,
              isHidden && isAdmin && 'opacity-40 grayscale-[40%]',
              !isAdmin && styles.hideMobile && 'block-hide-mobile',
              !isAdmin && styles.hideTablet && 'block-hide-tablet',
              !isAdmin && styles.hideDesktop && 'block-hide-desktop',
              isAdmin && !isSelected && 'hover:outline hover:outline-1 hover:outline-blue-400/60 hover:-outline-offset-1',
              isAdmin && isSelected && 'outline-2 outline-blue-600 -outline-offset-2 z-30 shadow-[0_0_0_1px_rgba(37,99,235,0.3)]',
              block.type !== 'container' && getShadowClass(styles.shadow),
              block.type !== 'container' && !HOVER_SCOPED_TO_INNER_ELEMENT.has(block.type) && getHoverClass(styles.hoverEffect)
            )}
            style={{
              ...(isAdmin ? wrapperStyles : {}),
              ...hoverStyles,
              ...(orderOverrides?.[block.id] !== undefined ? { order: orderOverrides[block.id] } : {}),
            } as any}
          >
            {/* suppressHydrationWarning: a background picture's optimizer URL carries the origin —
                FRONTEND_URL on the server, window.location on the client (shops without a media
                CDN). Where they differ, the text mismatch made React re-render the whole page
                client-side; the server's CSS is the right one to keep. */}
            {!isAdmin && respCss && <style suppressHydrationWarning>{respCss}</style>}

            {/* Floating Selection Overlay HUD: Label Badge */}
            {isSelected && (
              <div className="absolute -top-6 left-0 z-40 flex items-center gap-1.5 bg-blue-600 text-white text-[9px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-t-md shadow-md pointer-events-none select-none whitespace-nowrap">
                {depth > 0 && <span className="w-1.5 h-1.5 rounded-full bg-blue-200 inline-block" />}
                <span>{blockDisplayName(block)}</span>
                {block.type === 'container' && (
                  <span className="text-[8px] opacity-80 font-normal">({block.children?.length || 0})</span>
                )}
                {block.type === 'row' && (
                  <span className="text-[8px] opacity-80 font-normal">({block.settings?.columns || 2} kol.)</span>
                )}
              </div>
            )}

            {/* Floating Selection Overlay HUD: Action Toolbar */}
            {isSelected && (
              <div className="absolute -top-6 right-0 z-40 flex items-center gap-0.5 bg-white/95 backdrop-blur-sm shadow-md border border-slate-200/90 rounded-t-md p-0.5 pointer-events-auto select-none">
                {(block.type === 'container' || block.type === 'row') && (
                  <button
                    type="button"
                    title="Dodaj element do środka"
                    className="px-1.5 py-0.5 text-[9px] font-bold text-blue-700 hover:bg-blue-50 rounded flex items-center gap-0.5 transition-colors uppercase tracking-wider cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      if ((window as any).__prepareAdd) {
                        (window as any).__prepareAdd(block.id);
                      }
                    }}
                  >
                    <Plus className="w-3 h-3" /> Dodaj
                  </button>
                )}
                {onMoveBlockUp && (
                  <button
                    type="button"
                    title="Przesuń w górę"
                    className="p-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); onMoveBlockUp(block.id); }}
                  >
                    <ArrowUp className="w-3 h-3" />
                  </button>
                )}
                {onMoveBlockDown && (
                  <button
                    type="button"
                    title="Przesuń w dół"
                    className="p-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); onMoveBlockDown(block.id); }}
                  >
                    <ArrowDown className="w-3 h-3" />
                  </button>
                )}
                {onDuplicateBlock && (
                  <button
                    type="button"
                    title="Duplikuj"
                    className="p-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); onDuplicateBlock(block.id); }}
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                )}
                {onRemoveBlock && (
                  <button
                    type="button"
                    title="Usuń"
                    className="p-1 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); onRemoveBlock(block.id); }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}

            <div className="contents">
              <div className={cn(
                isAdmin
                  ? (isNested || isFullWidth ? 'w-full h-full' : `${maxWidthClass} mx-auto px-4 md:px-8 h-full`)
                  : (isNested ? 'w-full h-full' : respInnerClass)
              )}>
                {isHidden && isAdmin && (
                  <div className="absolute inset-0 bg-red-50/10 pointer-events-none flex items-start justify-end p-2 z-50">
                    <div className="bg-red-600 text-white text-[9px] font-black px-2 py-0.5 rounded shadow-sm uppercase tracking-tighter flex items-center gap-1 border border-white/20">
                      <EyeOff className="w-2.5 h-2.5" /> Ukryte na: {currentViewport.toUpperCase()}
                    </div>
                  </div>
                )}

                {block.gated_placeholder ? (
                  <div className="relative overflow-hidden rounded-2xl border border-slate-100 bg-slate-50/50 p-8 sm:p-12 text-center shadow-inner flex flex-col items-center justify-center min-h-[220px]">
                    <div className="absolute inset-0 bg-gradient-to-br from-violet-50/10 via-slate-100/10 to-indigo-50/10 blur-xl pointer-events-none" />
                    
                    <div className="relative z-10 space-y-4 max-w-sm">
                      <div className="w-12 h-12 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center mx-auto shadow-sm animate-bounce">
                        <Lock className="w-5 h-5" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="font-bold text-slate-800 text-base">
                          {block.gated_placeholder_text || block.settings?.gated_placeholder_text || "Zawartość zablokowana"}
                        </h4>
                        <p className="text-xs text-slate-400">Ta sekcja zawiera ekskluzywne materiały i informacje dostępne wyłącznie dla zarejestrowanych członków Klubu Łappy.</p>
                      </div>
                      <div className="pt-2">
                        <Link href="/logowanie">
                          <Button size="sm" className="bg-violet-600 hover:bg-violet-700 font-bold px-6 rounded-xl text-xs h-9 transition-all">
                            Zaloguj się / Załóż konto
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  (() => {
                    if (siteMode && isShopBlock(block.type)) {
                      return (
                        <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 px-4 py-6 text-center text-xs font-semibold text-amber-800">
                          Wymaga modułu Sklep — ten blok nie jest widoczny na stronie.
                        </div>
                      );
                    }
                    const RegisteredBlock = BLOCK_COMPONENTS[block.type];
                    if (!RegisteredBlock) return null;
                    // Live: the block (and anything nested in it) knows which breakpoints it's
                    // really shown on — hiding is CSS only, it still mounts (VisibleBreakpoints.tsx).
                    return (
                      <VisibleBreakpointsProvider styles={isAdmin ? undefined : styles}>
                        <RegisteredBlock {...sharedProps} />
                      </VisibleBreakpointsProvider>
                    );
                  })()
                )}
              </div>
            </div>
          </AnimatedBlockDiv>
        );
      })}

    </div>
  );

  // Top-level renderer only: nested ones see a subtree, not the page (see FirstProductGridContext).
  const firstProductGridId = !isNested && !isAdmin ? findFirstProductGridId(renderableBlocks) : null;
  const withFirstGrid = firstProductGridId
    ? <FirstProductGridContext.Provider value={firstProductGridId}>{renderedBlocks}</FirstProductGridContext.Provider>
    : renderedBlocks;

  const withProduct = activeProduct
    ? <ProductProvider product={activeProduct}>{withFirstGrid}</ProductProvider>
    : withFirstGrid;

  if (category) {
    return (
      <CatalogProvider
        subcategories={category.children || []}
        initialQuery={initialQuery}
        initialFilters={initialFilters}
        initialPage={initialPage}
        initialTotal={initialTotal}
        initialSort={initialSort}
        categoryId={category.is_all_products ? null : (category.id || null)}
      >
        {withProduct}
      </CatalogProvider>
    );
  }

  return withProduct;
};
